import { ApiError, isProblemDocument, NetworkError, type ProblemDocument } from './problem';

/**
 * The single place the dashboard talks to the API.
 *
 * It is a thin wrapper over `fetch`, not a framework, and it owns four things no call site should
 * have to remember:
 *
 * * the access token, and refreshing it exactly once when several requests hit a 401 together;
 * * `Accept-Language`, so every message comes back in the reader's language (NFR-006);
 * * `X-Request-Id`, so a screenshot from a receptionist can be traced to a log line;
 * * turning `application/problem+json` into a typed {@link ApiError}.
 *
 * Tokens are held in memory, and mirrored to a {@link TokenStore} when one is given. The
 * application gives it one scoped to the browser tab (auth/sessionPersistence.ts), so a reload
 * keeps the session and closing the tab ends it. Without a store, a reload means signing in again.
 */

export interface TokenSet {
  readonly accessToken: string;
  readonly accessTokenExpiresAt?: string;
  readonly refreshToken: string;
  readonly refreshTokenExpiresAt?: string;
}

/** Somewhere the tokens outlive the page, written on every change so a rotation is never lost. */
export interface TokenStore {
  load(): TokenSet | null;
  save(tokens: TokenSet | null): void;
}

export interface ApiClientOptions {
  readonly baseUrl: string;
  /** Supplies the language for `Accept-Language`; wired to i18next by the application. */
  readonly currentLocale: () => string;
  /** Called when the session cannot be recovered, so the app can return to sign-in. */
  readonly onSessionExpired?: () => void;
  readonly tokenStore?: TokenStore;
  readonly fetchImpl?: typeof fetch;
  readonly generateRequestId?: () => string;
}

export interface RequestOptions {
  readonly method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  readonly body?: unknown;
  readonly signal?: AbortSignal;
  /** Skips the token and the refresh dance; used by the sign-in endpoints themselves. */
  readonly anonymous?: boolean;
  readonly idempotencyKey?: string;
}

const REFRESH_PATH = '/auth/token/refresh';

export class ApiClient {
  private readonly options: ApiClientOptions;
  private readonly fetchImpl: typeof fetch;
  private tokens: TokenSet | null = null;
  /** In-flight refresh, shared so a burst of 401s produces one refresh, not one each. */
  private refreshInFlight: Promise<TokenSet | null> | null = null;

  constructor(options: ApiClientOptions) {
    this.options = options;
    this.fetchImpl = options.fetchImpl ?? globalThis.fetch.bind(globalThis);
    this.tokens = options.tokenStore?.load() ?? null;
  }

  setTokens(tokens: TokenSet | null): void {
    this.tokens = tokens;
    this.options.tokenStore?.save(tokens);
  }

  getTokens(): TokenSet | null {
    return this.tokens;
  }

  get isAuthenticated(): boolean {
    return this.tokens !== null;
  }

  async request<T>(path: string, options: RequestOptions = {}): Promise<T> {
    const response = await this.send(path, options);

    if (response.status === 401 && !options.anonymous && this.tokens) {
      const refreshed = await this.refreshOnce();
      if (refreshed) {
        const retried = await this.send(path, options);
        return this.toResult<T>(retried);
      }
      this.setTokens(null);
      this.options.onSessionExpired?.();
    }

    return this.toResult<T>(response);
  }

  get<T>(path: string, options: Omit<RequestOptions, 'method' | 'body'> = {}): Promise<T> {
    return this.request<T>(path, { ...options, method: 'GET' });
  }

  post<T>(path: string, body?: unknown, options: Omit<RequestOptions, 'method' | 'body'> = {}): Promise<T> {
    return this.request<T>(path, { ...options, method: 'POST', body });
  }

  delete<T>(path: string, options: Omit<RequestOptions, 'method' | 'body'> = {}): Promise<T> {
    return this.request<T>(path, { ...options, method: 'DELETE' });
  }

  /**
   * A multipart POST, for the one thing the dashboard sends that is not JSON: a photograph
   * (FR-506).
   *
   * It goes through the API rather than straight to the object store because the EXIF has to be
   * stripped server-side, and bytes that went directly to a bucket were never in a position to be
   * stripped (ADR-0019). `Content-Type` is deliberately left unset — the browser has to write the
   * multipart boundary into it, and setting it by hand produces a body the server cannot parse.
   */
  async upload<T>(path: string, form: FormData): Promise<T> {
    const send = async () => {
      const headers = new Headers({
        Accept: 'application/json, application/problem+json',
        'Accept-Language': this.options.currentLocale(),
        'X-Request-Id': this.newRequestId(),
      });
      if (this.tokens) {
        headers.set('Authorization', `Bearer ${this.tokens.accessToken}`);
      }
      try {
        return await this.fetchImpl(`${this.options.baseUrl}${path}`, {
          method: 'POST',
          headers,
          body: form,
          credentials: 'omit',
        });
      } catch (cause) {
        throw new NetworkError(cause);
      }
    };

    let response = await send();
    if (response.status === 401 && this.tokens && (await this.refreshOnce())) {
      response = await send();
    }
    return this.toResult<T>(response);
  }

  /**
   * A file, fetched with the caller's token (FR-1101 CSV export).
   *
   * An `<a download href>` cannot carry an Authorization header, and the alternative — putting the
   * access token in the query string — would write a credential into every proxy log between here
   * and the server. So the bytes are fetched like any other request, handed back as a Blob, and the
   * caller turns them into a download.
   *
   * Errors still arrive as JSON problems, so a refused export is reported the same way a refused
   * read is rather than saving a file containing an error message.
   */
  async download(path: string, options: Omit<RequestOptions, 'method' | 'body'> = {}): Promise<Blob> {
    let response = await this.send(path, { ...options, method: 'GET' });
    if (response.status === 401 && this.tokens && (await this.refreshOnce())) {
      response = await this.send(path, { ...options, method: 'GET' });
    }
    if (!response.ok) {
      return this.toResult<never>(response);
    }
    return response.blob();
  }

  // ── Internals ───────────────────────────────────────────────────────────

  private async send(path: string, options: RequestOptions): Promise<Response> {
    const headers = new Headers({
      Accept: 'application/json, application/problem+json',
      'Accept-Language': this.options.currentLocale(),
      'X-Request-Id': this.newRequestId(),
    });

    if (options.body !== undefined) {
      headers.set('Content-Type', 'application/json');
    }
    if (!options.anonymous && this.tokens) {
      headers.set('Authorization', `Bearer ${this.tokens.accessToken}`);
    }
    if (options.idempotencyKey) {
      headers.set('Idempotency-Key', options.idempotencyKey);
    }

    const init: RequestInit = {
      method: options.method ?? 'GET',
      headers,
      // Authentication is a bearer token, never a cookie, so no credentials cross the origin.
      credentials: 'omit',
    };
    if (options.body !== undefined) {
      init.body = JSON.stringify(options.body);
    }
    if (options.signal) {
      init.signal = options.signal;
    }

    try {
      return await this.fetchImpl(`${this.options.baseUrl}${path}`, init);
    } catch (cause) {
      throw new NetworkError(cause);
    }
  }

  /** Refreshes at most once, however many callers are waiting. */
  private refreshOnce(): Promise<TokenSet | null> {
    this.refreshInFlight ??= this.performRefresh().finally(() => {
      this.refreshInFlight = null;
    });
    return this.refreshInFlight;
  }

  private async performRefresh(): Promise<TokenSet | null> {
    const refreshToken = this.tokens?.refreshToken;
    if (!refreshToken) {
      return null;
    }

    const response = await this.send(REFRESH_PATH, {
      method: 'POST',
      body: { refreshToken },
      anonymous: true,
    });

    if (!response.ok) {
      return null;
    }

    const payload = (await response.json()) as {
      accessToken?: string;
      accessTokenExpiresAt?: string;
      refreshToken?: string;
      refreshTokenExpiresAt?: string;
    };

    if (!payload.accessToken || !payload.refreshToken) {
      return null;
    }

    const tokens: TokenSet = {
      accessToken: payload.accessToken,
      refreshToken: payload.refreshToken,
      ...(payload.accessTokenExpiresAt ? { accessTokenExpiresAt: payload.accessTokenExpiresAt } : {}),
      ...(payload.refreshTokenExpiresAt ? { refreshTokenExpiresAt: payload.refreshTokenExpiresAt } : {}),
    };
    // Saved before anything else runs: the old refresh token is now spent, and a reload that found
    // it in storage would present it again and have the server revoke the whole session as theft.
    this.setTokens(tokens);
    return tokens;
  }

  private async toResult<T>(response: Response): Promise<T> {
    if (response.status === 204 || response.headers.get('Content-Length') === '0') {
      return undefined as T;
    }

    const text = await response.text();
    const payload: unknown = text ? safeParse(text) : undefined;

    if (!response.ok) {
      const problem: ProblemDocument = isProblemDocument(payload)
        ? payload
        : { title: response.statusText, status: response.status };
      throw new ApiError(response.status, {
        ...problem,
        requestId: problem.requestId ?? response.headers.get('X-Request-Id') ?? undefined,
      });
    }

    return payload as T;
  }

  private newRequestId(): string {
    if (this.options.generateRequestId) {
      return this.options.generateRequestId();
    }
    if (globalThis.crypto?.randomUUID) {
      return globalThis.crypto.randomUUID();
    }
    return `dash-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  }
}

function safeParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return { detail: text };
  }
}
